import { createTheme, alpha } from "@mui/material/styles";

// Design Tokens
export const tokens = {
  // Animation tokens
  animation: {
    easeOutExpo: "cubic-bezier(0.16, 1, 0.3, 1)",
    easeInOut: "cubic-bezier(0.4, 0, 0.2, 1)",
    easeOut: "cubic-bezier(0, 0, 0.2, 1)",
    easeIn: "cubic-bezier(0.4, 0, 1, 1)",
    spring: "cubic-bezier(0.34, 1.56, 0.64, 1)",
    durationFast: "150ms",
    durationNormal: "250ms",
    durationSlow: "400ms",
    durationSlowest: "600ms",
  },
  // Spacing scale (base 4px)
  spacing: {
    xxs: 4,
    xs: 8,
    sm: 12,
    md: 16,
    lg: 24,
    xl: 32,
    xxl: 48,
    xxxl: 64,
  },
  // Border radius
  radius: {
    sm: 8,
    md: 12,
    lg: 16,
    xl: 20,
    xxl: 24,
    pill: 9999,
  },
  // Z-index scale
  zIndex: {
    drawer: 1200,
    modal: 1300,
    snackbar: 1400,
    tooltip: 1500,
  },
};

// Color palette for light and dark modes
const getDesignTokens = (mode) => ({
  palette: {
    mode,
    primary: {
      main: mode === "light" ? "#4F46E5" : "#818CF8",
      light: "#818CF8",
      dark: "#3730A3",
      contrastText: "#ffffff",
    },
    secondary: {
      main: mode === "light" ? "#8B5CF6" : "#A78BFA",
      light: "#A78BFA",
      dark: "#7C3AED",
      contrastText: "#ffffff",
    },
    background: {
      default: mode === "light" ? "#F8FAFC" : "#0F172A",
      paper: mode === "light" ? "#FFFFFF" : "#1E293B",
      subtle: mode === "light" ? "#F1F5F9" : "#334155",
    },
    text: {
      primary: mode === "light" ? "#111827" : "#F1F5F9",
      secondary: mode === "light" ? "#6B7280" : "#94A3B8",
      disabled: mode === "light" ? "#9CA3AF" : "#64748B",
    },
    divider: mode === "light" ? "#E5E7EB" : "#334155",
    success: {
      main: mode === "light" ? "#10B981" : "#34D399",
      light: "#34D399",
      dark: "#059669",
      contrastText: "#ffffff",
    },
    warning: {
      main: mode === "light" ? "#F59E0B" : "#FBBF24",
      light: "#FBBF24",
      dark: "#D97706",
      contrastText: "#000000",
    },
    error: {
      main: mode === "light" ? "#EF4444" : "#F87171",
      light: "#F87171",
      dark: "#DC2626",
      contrastText: "#ffffff",
    },
    info: {
      main: mode === "light" ? "#3B82F6" : "#60A5FA",
      light: "#60A5FA",
      dark: "#2563EB",
      contrastText: "#ffffff",
    },
    grey: {
      50: "#F9FAFB",
      100: "#F3F4F6",
      200: "#E5E7EB",
      300: "#D1D5DB",
      400: "#9CA3AF",
      500: "#6B7280",
      600: "#4B5563",
      700: "#374151",
      800: "#1F2937",
      900: "#111827",
      950: "#030712",
    },
    // Custom semantic colors
    moneyIn: {
      main: mode === "light" ? "#10B981" : "#34D399",
      light: mode === "light" ? "#D1FAE5" : "#064E3B",
      dark: "#059669",
    },
    moneyOut: {
      main: mode === "light" ? "#EF4444" : "#F87171",
      light: mode === "light" ? "#FEE2E2" : "#7F1D1D",
      dark: "#DC2626",
    },
    // Card backgrounds for glassmorphism
    glass: {
      background:
        mode === "light" ? "rgba(255, 255, 255, 0.8)" : "rgba(30, 41, 59, 0.8)",
      border:
        mode === "light"
          ? "rgba(255, 255, 255, 0.5)"
          : "rgba(148, 163, 184, 0.1)",
    },
  },
});

// Create theme function that accepts mode
export const createAppTheme = (mode = "light") => {
  const designTokens = getDesignTokens(mode);

  return createTheme({
    ...designTokens,
    typography: {
      fontFamily: "'Inter', 'Plus Jakarta Sans', 'Roboto', sans-serif",
      h1: {
        fontWeight: 800,
        letterSpacing: "-0.025em",
        fontSize: "2.5rem",
        lineHeight: 1.2,
      },
      h2: {
        fontWeight: 700,
        letterSpacing: "-0.025em",
        fontSize: "2rem",
        lineHeight: 1.3,
      },
      h3: {
        fontWeight: 700,
        letterSpacing: "-0.02em",
        fontSize: "1.5rem",
        lineHeight: 1.4,
      },
      h4: {
        fontWeight: 600,
        letterSpacing: "-0.015em",
        fontSize: "1.25rem",
        lineHeight: 1.4,
      },
      h5: {
        fontWeight: 600,
        letterSpacing: "-0.01em",
        fontSize: "1.125rem",
        lineHeight: 1.5,
      },
      h6: {
        fontWeight: 600,
        letterSpacing: "-0.01em",
        fontSize: "1rem",
        lineHeight: 1.5,
      },
      subtitle1: {
        fontWeight: 500,
        letterSpacing: "0.01em",
        fontSize: "1rem",
        lineHeight: 1.5,
      },
      subtitle2: {
        fontWeight: 500,
        letterSpacing: "0.01em",
        fontSize: "0.875rem",
        lineHeight: 1.57,
      },
      body1: {
        fontSize: "1rem",
        lineHeight: 1.6,
      },
      body2: {
        fontSize: "0.875rem",
        lineHeight: 1.57,
      },
      button: {
        fontWeight: 600,
        textTransform: "none",
        letterSpacing: "0.02em",
      },
      caption: {
        fontSize: "0.75rem",
        lineHeight: 1.5,
        fontWeight: 500,
      },
      overline: {
        fontWeight: 600,
        letterSpacing: "0.08em",
        fontSize: "0.75rem",
        textTransform: "uppercase",
      },
    },
    shape: {
      borderRadius: tokens.radius.md,
    },
    shadows: [
      "none",
      mode === "light"
        ? "0px 1px 2px rgba(0, 0, 0, 0.05)"
        : "0px 1px 2px rgba(0, 0, 0, 0.3)",
      mode === "light"
        ? "0px 4px 6px -1px rgba(0, 0, 0, 0.05), 0px 2px 4px -1px rgba(0, 0, 0, 0.03)"
        : "0px 4px 6px -1px rgba(0, 0, 0, 0.4), 0px 2px 4px -1px rgba(0, 0, 0, 0.2)",
      mode === "light"
        ? "0px 10px 15px -3px rgba(0, 0, 0, 0.05), 0px 4px 6px -2px rgba(0, 0, 0, 0.025)"
        : "0px 10px 15px -3px rgba(0, 0, 0, 0.4), 0px 4px 6px -2px rgba(0, 0, 0, 0.2)",
      mode === "light"
        ? "0px 20px 25px -5px rgba(0, 0, 0, 0.05), 0px 10px 10px -5px rgba(0, 0, 0, 0.02)"
        : "0px 20px 25px -5px rgba(0, 0, 0, 0.4), 0px 10px 10px -5px rgba(0, 0, 0, 0.2)",
      mode === "light"
        ? "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)"
        : "0 4px 6px -1px rgba(0, 0, 0, 0.5), 0 2px 4px -1px rgba(0, 0, 0, 0.3)",
      ...Array(19).fill("none"),
    ],
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            backgroundColor: mode === "light" ? "#F8FAFC" : "#0F172A",
            transition: `background-color ${tokens.animation.durationNormal} ${tokens.animation.easeInOut}`,
          },
          // Smooth color scheme transition
          "*": {
            transition: `background-color ${tokens.animation.durationNormal} ${tokens.animation.easeInOut},
                         border-color ${tokens.animation.durationNormal} ${tokens.animation.easeInOut},
                         color ${tokens.animation.durationFast} ${tokens.animation.easeInOut}`,
          },
          // Custom scrollbar
          "::-webkit-scrollbar": {
            width: "8px",
            height: "8px",
          },
          "::-webkit-scrollbar-track": {
            background: mode === "light" ? "#F1F5F9" : "#1E293B",
            borderRadius: "4px",
          },
          "::-webkit-scrollbar-thumb": {
            background: mode === "light" ? "#CBD5E1" : "#475569",
            borderRadius: "4px",
            "&:hover": {
              background: mode === "light" ? "#94A3B8" : "#64748B",
            },
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.pill,
            padding: "10px 24px",
            boxShadow: "none",
            transition: `all ${tokens.animation.durationNormal} ${tokens.animation.easeOutExpo}`,
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow:
                mode === "light"
                  ? "0 4px 12px rgba(0, 0, 0, 0.1)"
                  : "0 4px 12px rgba(0, 0, 0, 0.4)",
            },
            "&:active": {
              transform: "translateY(0)",
            },
          },
          containedPrimary: {
            background:
              mode === "light"
                ? "linear-gradient(135deg, #4F46E5 0%, #4338CA 100%)"
                : "linear-gradient(135deg, #818CF8 0%, #6366F1 100%)",
            "&:hover": {
              background:
                mode === "light"
                  ? "linear-gradient(135deg, #4338CA 0%, #3730A3 100%)"
                  : "linear-gradient(135deg, #6366F1 0%, #4F46E5 100%)",
              boxShadow: "0 4px 16px rgba(79, 70, 229, 0.4)",
            },
          },
          containedSecondary: {
            background: "linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #7C3AED 0%, #6D28D9 100%)",
              boxShadow: "0 4px 16px rgba(139, 92, 246, 0.4)",
            },
          },
          containedSuccess: {
            background: "linear-gradient(135deg, #10B981 0%, #059669 100%)",
            "&:hover": {
              background: "linear-gradient(135deg, #059669 0%, #047857 100%)",
              boxShadow: "0 4px 16px rgba(16, 185, 129, 0.4)",
            },
          },
          outlined: {
            borderWidth: "1.5px",
            "&:hover": {
              borderWidth: "1.5px",
              backgroundColor:
                mode === "light"
                  ? alpha("#4F46E5", 0.04)
                  : alpha("#818CF8", 0.08),
            },
          },
          text: {
            "&:hover": {
              backgroundColor:
                mode === "light"
                  ? alpha("#4F46E5", 0.04)
                  : alpha("#818CF8", 0.08),
            },
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: "none",
            backgroundColor: mode === "light" ? "#FFFFFF" : "#1E293B",
            transition: `all ${tokens.animation.durationNormal} ${tokens.animation.easeInOut}`,
            "&.MuiPaper-elevation1": {
              boxShadow:
                mode === "light"
                  ? "0px 1px 3px rgba(0, 0, 0, 0.08), 0px 1px 2px rgba(0, 0, 0, 0.04)"
                  : "0px 1px 3px rgba(0, 0, 0, 0.3), 0px 1px 2px rgba(0, 0, 0, 0.2)",
            },
            "&.MuiPaper-elevation2": {
              boxShadow:
                mode === "light"
                  ? "0px 10px 30px -5px rgba(0, 0, 0, 0.06)"
                  : "0px 10px 30px -5px rgba(0, 0, 0, 0.4)",
            },
          },
          rounded: {
            borderRadius: tokens.radius.xl,
          },
        },
      },
      MuiCard: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.xl,
            border:
              mode === "light"
                ? "1px solid rgba(0, 0, 0, 0.05)"
                : "1px solid rgba(255, 255, 255, 0.05)",
            boxShadow:
              mode === "light"
                ? "0px 4px 20px rgba(0, 0, 0, 0.03)"
                : "0px 4px 20px rgba(0, 0, 0, 0.2)",
            transition: `all ${tokens.animation.durationNormal} ${tokens.animation.easeOutExpo}`,
            "&:hover": {
              boxShadow:
                mode === "light"
                  ? "0px 8px 30px rgba(0, 0, 0, 0.08)"
                  : "0px 8px 30px rgba(0, 0, 0, 0.3)",
            },
          },
        },
      },
      MuiTextField: {
        styleOverrides: {
          root: {
            "& .MuiOutlinedInput-root": {
              borderRadius: tokens.radius.md,
              backgroundColor: mode === "light" ? "#FFFFFF" : "#334155",
              transition: `all ${tokens.animation.durationNormal} ${tokens.animation.easeInOut}`,
              "& fieldset": {
                borderColor: mode === "light" ? "#E5E7EB" : "#475569",
              },
              "&:hover fieldset": {
                borderColor: mode === "light" ? "#9CA3AF" : "#64748B",
              },
              "&.Mui-focused fieldset": {
                borderColor: mode === "light" ? "#4F46E5" : "#818CF8",
                boxShadow:
                  mode === "light"
                    ? "0 0 0 4px rgba(79, 70, 229, 0.1)"
                    : "0 0 0 4px rgba(129, 140, 248, 0.2)",
              },
            },
          },
        },
      },
      MuiListItemButton: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.md,
            margin: "2px 8px",
            transition: `all ${tokens.animation.durationFast} ${tokens.animation.easeInOut}`,
            "&:hover": {
              backgroundColor:
                mode === "light"
                  ? alpha("#4F46E5", 0.06)
                  : alpha("#818CF8", 0.1),
            },
            "&.Mui-selected": {
              backgroundColor:
                mode === "light"
                  ? alpha("#4F46E5", 0.1)
                  : alpha("#818CF8", 0.15),
              "&:hover": {
                backgroundColor:
                  mode === "light"
                    ? alpha("#4F46E5", 0.15)
                    : alpha("#818CF8", 0.2),
              },
            },
          },
        },
      },
      MuiListItemIcon: {
        styleOverrides: {
          root: {
            minWidth: 40,
            color: mode === "light" ? "#6B7280" : "#94A3B8",
          },
        },
      },
      MuiChip: {
        styleOverrides: {
          root: {
            fontWeight: 600,
            borderRadius: tokens.radius.sm,
            transition: `all ${tokens.animation.durationFast} ${tokens.animation.easeInOut}`,
          },
          filled: {
            backgroundColor: mode === "light" ? "#F3F4F6" : "#334155",
          },
        },
      },
      MuiTableRow: {
        styleOverrides: {
          root: {
            transition: `background-color ${tokens.animation.durationFast} ${tokens.animation.easeInOut}`,
            "&:hover": {
              backgroundColor:
                mode === "light"
                  ? alpha("#4F46E5", 0.02)
                  : alpha("#818CF8", 0.05),
            },
          },
        },
      },
      MuiTableCell: {
        styleOverrides: {
          root: {
            borderColor: mode === "light" ? "#E5E7EB" : "#334155",
          },
          head: {
            fontWeight: 600,
            backgroundColor: mode === "light" ? "#F8FAFC" : "#1E293B",
          },
        },
      },
      MuiTooltip: {
        styleOverrides: {
          tooltip: {
            backgroundColor: mode === "light" ? "#1F2937" : "#F1F5F9",
            color: mode === "light" ? "#F9FAFB" : "#111827",
            fontSize: "0.75rem",
            fontWeight: 500,
            borderRadius: tokens.radius.sm,
            padding: "8px 12px",
          },
          arrow: {
            color: mode === "light" ? "#1F2937" : "#F1F5F9",
          },
        },
      },
      MuiDialog: {
        styleOverrides: {
          paper: {
            borderRadius: tokens.radius.xl,
          },
        },
      },
      MuiDrawer: {
        styleOverrides: {
          paper: {
            backgroundColor: mode === "light" ? "#FFFFFF" : "#1E293B",
            borderRight:
              mode === "light" ? "1px solid #E5E7EB" : "1px solid #334155",
          },
        },
      },
      MuiAppBar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "light" ? "#FFFFFF" : "#1E293B",
            color: mode === "light" ? "#111827" : "#F1F5F9",
            boxShadow:
              mode === "light"
                ? "0 1px 3px rgba(0, 0, 0, 0.05)"
                : "0 1px 3px rgba(0, 0, 0, 0.3)",
          },
        },
      },
      MuiTabs: {
        styleOverrides: {
          indicator: {
            height: 3,
            borderRadius: "3px 3px 0 0",
          },
        },
      },
      MuiTab: {
        styleOverrides: {
          root: {
            textTransform: "none",
            fontWeight: 600,
            transition: `color ${tokens.animation.durationFast} ${tokens.animation.easeInOut}`,
          },
        },
      },
      MuiAlert: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.md,
          },
          standardSuccess: {
            backgroundColor:
              mode === "light" ? "#D1FAE5" : alpha("#10B981", 0.15),
            color: mode === "light" ? "#065F46" : "#34D399",
          },
          standardError: {
            backgroundColor:
              mode === "light" ? "#FEE2E2" : alpha("#EF4444", 0.15),
            color: mode === "light" ? "#991B1B" : "#F87171",
          },
          standardWarning: {
            backgroundColor:
              mode === "light" ? "#FEF3C7" : alpha("#F59E0B", 0.15),
            color: mode === "light" ? "#92400E" : "#FBBF24",
          },
          standardInfo: {
            backgroundColor:
              mode === "light" ? "#DBEAFE" : alpha("#3B82F6", 0.15),
            color: mode === "light" ? "#1E40AF" : "#60A5FA",
          },
        },
      },
      MuiSkeleton: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "light" ? "#E5E7EB" : "#334155",
          },
        },
      },
      MuiBadge: {
        styleOverrides: {
          badge: {
            fontWeight: 600,
            fontSize: "0.7rem",
          },
        },
      },
      MuiAvatar: {
        styleOverrides: {
          root: {
            backgroundColor: mode === "light" ? "#E5E7EB" : "#475569",
          },
        },
      },
      MuiLinearProgress: {
        styleOverrides: {
          root: {
            borderRadius: tokens.radius.pill,
            backgroundColor: mode === "light" ? "#E5E7EB" : "#334155",
          },
          bar: {
            borderRadius: tokens.radius.pill,
          },
        },
      },
      MuiCircularProgress: {
        styleOverrides: {
          root: {
            // Add animation customization if needed
          },
        },
      },
    },
    // Custom mixins
    customMixins: {
      glassmorphism: {
        background:
          mode === "light"
            ? "rgba(255, 255, 255, 0.8)"
            : "rgba(30, 41, 59, 0.8)",
        backdropFilter: "blur(10px)",
        border:
          mode === "light"
            ? "1px solid rgba(255, 255, 255, 0.5)"
            : "1px solid rgba(148, 163, 184, 0.1)",
      },
      cardHover: {
        transition: `all ${tokens.animation.durationNormal} ${tokens.animation.easeOutExpo}`,
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow:
            mode === "light"
              ? "0 20px 40px rgba(0, 0, 0, 0.1)"
              : "0 20px 40px rgba(0, 0, 0, 0.4)",
        },
      },
    },
  });
};

// Create default light theme for backwards compatibility
const theme = createAppTheme("light");

export default theme;
